import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Select, Space, Typography, Divider, App, Alert } from 'antd';
import { trpc } from '../../lib/api';
import { FileUploader } from '../file/FileUploader';
import { ACCEPTED_DOCUMENT_LABEL, ACCEPTED_DOCUMENT_TYPES } from '../../constants/documentAccept';
import { LocationTreeSelect } from '../common/LocationTreeSelect';
import { BuildingLocationSelect } from '../common/BuildingLocationSelect';
import { SingleUserSelect } from '../common/SingleUserSelect';
import { RichTextEditor } from '../common/RichTextEditor';
import { withDevModalFilePath } from '../common';

const { Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/document/DocumentLedgerCreateModal.tsx');

const ROLE_PRIORITY: Record<string, number> = {
  viewer: 0,
  user: 1,
  manager: 2,
  admin: 3,
};

type DocumentAssociationEntityType = 'location' | 'equipment' | 'part' | 'supplier' | 'user';
type DocumentRelatedEntityType = DocumentAssociationEntityType | 'location_building' | 'purchase_order';
type DocumentTypeOption = {
  id: string;
  typeKey?: string | null;
  name: string;
  category: 'general' | 'official';
  isEnabled?: boolean;
  isSystem?: boolean;
  relatedEntityType?: DocumentRelatedEntityType | null;
  readRole?: 'viewer' | 'user' | 'manager' | 'admin' | null;
  writeRole?: 'viewer' | 'user' | 'manager' | 'admin' | null;
};

type EquipmentSummary = { id: string; name?: string | null; displayId?: string | null };
type PartSummary = { id: string; name?: string | null; displayId?: string | null };
type SupplierSummary = { id: string; name?: string | null };
type UploadedDocumentFile = {
  id?: string;
  name?: string;
  originalName?: string;
  fileName?: string;
};

const ENTITY_TYPE_LABEL_MAP: Record<DocumentRelatedEntityType, string> = {
  location: 'エリア',
  location_building: 'エリア（建物）',
  equipment: '設備',
  part: '部品',
  supplier: '業者',
  user: 'ユーザー',
  purchase_order: '発注',
};

interface DocumentLedgerCreateModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

const normalizeItems = <T,>(data?: unknown) => {
  if (!data || typeof data !== 'object') return [];
  const collection = data as { items?: unknown; data?: unknown; users?: unknown };
  if (Array.isArray(collection.items)) return collection.items as T[];
  if (Array.isArray(collection.data)) return collection.data as T[];
  if (Array.isArray(collection.users)) return collection.users as T[];
  return [];
};

export const DocumentLedgerCreateModal: React.FC<DocumentLedgerCreateModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const utils = trpc.useUtils();
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string }>>([]);

  const { data: profile } = trpc.user.getProfile.useQuery();
  const canManageUsers = ['admin', 'manager'].includes(profile?.role ?? '');
  const actorRole = profile?.role ?? 'viewer';

  const entityType = Form.useWatch('entityType', form) as DocumentAssociationEntityType | undefined;
  const entityId = Form.useWatch('entityId', form) as string | undefined;
  const documentTypeId = Form.useWatch('documentTypeId', form) as string | undefined;

  const { data: documentTypes = [], isLoading: isTypesLoading } = trpc.document.listTypes.useQuery();
  const typedDocumentTypes = documentTypes as DocumentTypeOption[];
  const selectedDocumentType = useMemo(
    () => typedDocumentTypes.find((type) => type.id === documentTypeId) ?? null,
    [typedDocumentTypes, documentTypeId]
  );
  const fixedRelatedEntityType = selectedDocumentType?.relatedEntityType ?? null;
  const fixedAssociationType = fixedRelatedEntityType === 'location_building'
    ? 'location'
    : fixedRelatedEntityType === 'purchase_order'
      ? null
    : fixedRelatedEntityType;
  const useBuildingList = fixedRelatedEntityType === 'location_building'
    || (selectedDocumentType?.typeKey
      ? ['statutory_special_building_survey', 'statutory_building_equipment_inspection', 'statutory_fire_protection_inspection']
        .includes(selectedDocumentType.typeKey)
      : false);

  const isRelatedTypeCompatible = React.useCallback((
    relatedType: DocumentRelatedEntityType | null | undefined,
    associationType?: DocumentAssociationEntityType
  ) => {
    if (!relatedType) return true;
    if (!associationType) return false;
    if (relatedType === 'location_building') {
      return associationType === 'location';
    }
    if (relatedType === 'purchase_order') {
      return false;
    }
    return relatedType === associationType;
  }, []);

  const hasMinimumRole = React.useCallback((role: string, minimum: string) =>
    (ROLE_PRIORITY[role] ?? 0) >= (ROLE_PRIORITY[minimum] ?? 0), []);

  const enabledDocumentTypes = useMemo(() => {
    return typedDocumentTypes.filter(
      (type) => type.isEnabled !== false && hasMinimumRole(actorRole, type.writeRole ?? 'user')
        && type.relatedEntityType !== 'purchase_order'
    );
  }, [typedDocumentTypes, actorRole, hasMinimumRole]);

  const { data: equipmentData } = trpc.equipment.list.useQuery(
    { page: 1, limit: 200 },
    { enabled: open && entityType === 'equipment' },
  );
  const { data: partData } = trpc.parts.list.useQuery(
    { page: 1, limit: 200 },
    { enabled: open && entityType === 'part' },
  );
  const { data: supplierData } = trpc.suppliers.list.useQuery(
    { page: 1, limit: 200 },
    { enabled: open && entityType === 'supplier' },
  );

  const equipmentOptions = useMemo(() => {
    const items = normalizeItems<EquipmentSummary>(equipmentData);
    return items.map((item) => ({
      label: item.displayId ? `${item.name ?? item.id} (${item.displayId})` : (item.name ?? item.id),
      value: item.id,
    }));
  }, [equipmentData]);

  const partOptions = useMemo(() => {
    const items = normalizeItems<PartSummary>(partData);
    return items.map((item) => ({
      label: item.displayId ? `${item.name ?? item.id} (${item.displayId})` : (item.name ?? item.id),
      value: item.id,
    }));
  }, [partData]);

  const supplierOptions = useMemo(() => {
    const items = normalizeItems<SupplierSummary>(supplierData);
    return items.map((item) => ({
      label: item.name ?? item.id,
      value: item.id,
    }));
  }, [supplierData]);

  const createMutation = trpc.document.create.useMutation({
    onSuccess: async () => {
      message.success('書類を登録しました');
      setUploadedFiles([]);
      form.resetFields();
      await utils.document.list.invalidate();
      onSuccess?.();
    },
    onError: (error) => {
      message.error(error.message || '書類登録に失敗しました');
    },
  });

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ entityType: 'location' });
    setUploadedFiles([]);
  }, [open, form]);

  useEffect(() => {
    if (!open) return;
    if (entityType) {
      const conflictsWithEntity = selectedDocumentType?.relatedEntityType
        && !isRelatedTypeCompatible(selectedDocumentType.relatedEntityType, entityType);
      const conflictsWithOfficial = !selectedDocumentType?.relatedEntityType
        && selectedDocumentType?.category === 'official'
        && entityType !== 'location';

      if (conflictsWithEntity || conflictsWithOfficial) {
        form.setFieldsValue({ documentTypeId: undefined });
      }
    }
    form.setFieldsValue({ entityId: undefined });
  }, [entityType, open, form, selectedDocumentType, isRelatedTypeCompatible]);

  useEffect(() => {
    if (!open) return;
    if (fixedAssociationType && entityType !== fixedAssociationType) {
      form.setFieldsValue({ entityType: fixedAssociationType, entityId: undefined });
    }
  }, [fixedAssociationType, entityType, form, open]);

  useEffect(() => {
    if (!open) return;
    if (useBuildingList && entityType === 'location') {
      form.setFieldsValue({ entityId: undefined });
    }
  }, [useBuildingList, entityType, form, open]);

  const applyTitleFromUpload = (file: UploadedDocumentFile | null | undefined) => {
    const currentTitle = form.getFieldValue('title');
    if (currentTitle) return;
    const inferredTitle = file?.originalName ?? file?.fileName ?? file?.name;
    if (inferredTitle) {
      const dotIndex = inferredTitle.lastIndexOf('.');
      const title = dotIndex > 0 ? inferredTitle.slice(0, dotIndex) : inferredTitle;
      form.setFieldsValue({ title });
    }
  };

  const normalizeDocumentFileName = (fileName?: string | null) => {
    const safeName = (fileName ?? '').trim();
    if (!safeName) return '';
    return safeName.toLowerCase();
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    if (uploadedFiles.length === 0) {
      message.warning('ファイルをアップロードしてください');
      return;
    }

    createMutation.mutate({
      title: values.title,
      description: values.description ?? undefined,
      documentTypeId: values.documentTypeId,
      association: {
        entityType: values.entityType,
        entityId: values.entityId,
      },
      files: uploadedFiles.map((file) => ({ fileId: file.id })),
    });
  };

  const entityTypeOptions: { label: string; value: DocumentAssociationEntityType }[] = useMemo(() => [
    { label: 'エリア', value: 'location' },
    { label: '設備', value: 'equipment' },
    { label: '部品', value: 'part' },
    { label: '業者', value: 'supplier' },
    ...(canManageUsers
      ? [{ label: 'ユーザー', value: 'user' as DocumentAssociationEntityType }]
      : []),
  ], [canManageUsers]);

  const resolvedEntityTypeOptions = useMemo(() => {
    if (!fixedAssociationType) return entityTypeOptions;
    const exists = entityTypeOptions.some((option) => option.value === fixedAssociationType);
    if (exists) return entityTypeOptions;
    return [...entityTypeOptions, { label: ENTITY_TYPE_LABEL_MAP[fixedAssociationType], value: fixedAssociationType }];
  }, [entityTypeOptions, fixedAssociationType]);

  const renderEntitySelector = () => {
    if (!entityType) {
      return (
        <Text type="secondary">関連タイプを選択してください</Text>
      );
    }

    switch (entityType) {
      case 'location':
        return useBuildingList
          ? <BuildingLocationSelect placeholder="建物タイプのエリアを選択" />
          : <LocationTreeSelect placeholder="エリアを選択" />;
      case 'equipment':
        return (
          <Select
            showSearch
            allowClear
            placeholder="設備を選択"
            options={equipmentOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      case 'part':
        return (
          <Select
            showSearch
            allowClear
            placeholder="部品・消耗品を選択"
            options={partOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      case 'supplier':
        return (
          <Select
            showSearch
            allowClear
            placeholder="業者を選択"
            options={supplierOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      case 'user':
        return <SingleUserSelect showAssignMeButton />;
      default:
        return null;
    }
  };

  const hasTypes = enabledDocumentTypes.length > 0;

  return (
    <Modal
      title="書類登録"
      open={open}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      okText="登録"
      cancelText="キャンセル"
      confirmLoading={createMutation.isPending}
      okButtonProps={{ disabled: !hasTypes }}
      destroyOnHidden
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {!hasTypes && !isTypesLoading && (
          <Alert
            type="info"
            message="利用可能な書類カテゴリがありません"
            description="書類カテゴリの設定が有効化されていないため、書類を登録できません。"
            showIcon
          />
        )}
        <Text type="secondary">アップロード済み: {uploadedFiles.length}件</Text>
        <FileUploader
          entityType={entityType}
          entityId={entityId}
          category="document"
          accept={ACCEPTED_DOCUMENT_TYPES}
          acceptLabel={ACCEPTED_DOCUMENT_LABEL}
          maxFiles={10}
          maxSize={25}
          disallowDuplicateNames
          existingFileNames={uploadedFiles.map((file) => file.name)}
          normalizeDuplicateName={normalizeDocumentFileName}
          onUploadSuccess={(file) => {
            if (file?.id) {
              const name = (file?.originalName ?? file?.fileName ?? file?.name ?? '').toString();
              setUploadedFiles((prev) => {
                const normalized = normalizeDocumentFileName(name);
                const exists = prev.some((item) => normalizeDocumentFileName(item.name) === normalized);
                if (exists) {
                  message.error('同名ファイルはアップロードできません');
                  return prev;
                }
                return [...prev, { id: file.id, name }];
              });
            }
            applyTitleFromUpload(file);
          }}
        />
        <Divider style={{ margin: '8px 0' }} />
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="書類名"
            rules={[{ required: true, message: '書類名を入力してください' }]}
          >
            <Input placeholder="例: 点検報告書 2025" maxLength={255} />
          </Form.Item>
          <Form.Item
            name="description"
            label="説明"
            valuePropName="value"
            trigger="onChange"
          >
            <RichTextEditor placeholder="書類の詳細や補足を記載" minHeight={160} />
          </Form.Item>
          <Form.Item
            name="documentTypeId"
            label="書類カテゴリ"
            rules={[{ required: true, message: '書類カテゴリを選択してください' }]}
          >
            <Select
              placeholder={isTypesLoading ? '読み込み中...' : '書類カテゴリを選択'}
              loading={isTypesLoading}
              options={enabledDocumentTypes.map((type) => ({
                label: type.name,
                value: type.id,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="entityType"
            label="関連タイプ"
            rules={[{ required: true, message: '関連タイプを選択してください' }]}
          >
            <Select options={resolvedEntityTypeOptions} disabled={Boolean(fixedRelatedEntityType)} />
          </Form.Item>
          <Form.Item
            name="entityId"
            label="関連先"
            rules={[{ required: true, message: '関連先を選択してください' }]}
          >
            {renderEntitySelector()}
          </Form.Item>
        </Form>
        {fixedRelatedEntityType && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            選択中の書類カテゴリは「{ENTITY_TYPE_LABEL_MAP[fixedRelatedEntityType]}」に固定されています。
          </Text>
        )}
        {!fixedRelatedEntityType && entityType && entityType !== 'location' && selectedDocumentType?.isSystem
          && selectedDocumentType.category === 'official' && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              法定点検3種は「エリア（建物）」にのみ登録できます。
            </Text>
          )}
      </Space>
    </Modal>
  );
};

export default DocumentLedgerCreateModal;
